// frontend/src/admin/v2/AdminDashboard.jsx
import { Stack, Typography, Alert } from '@mui/material'
import { useDashboardSummary } from './hooks/useDashboardSummary'
import RecentFailuresCard from './components/RecentFailuresCard'
import KpiRow, { KpiCell } from '../../components/common/KpiRow'
import StatTile from '../../components/common/StatTile'
import { SP } from '../../theme/tokens'
import { formatBerlinTime } from '../../utils/time'
import { formatPercent } from '../../utils/format'

/**
 * AdminDashboard — entry page at /admin.
 *
 * @description
 *   At-a-glance health panel: FOUR KPI tiles (sync success, failed jobs,
 *   webhooks received, email delivery — each over the last 24 h) in one
 *   `KpiRow`, plus a recent-failures list. Polled by `useDashboardSummary`
 *   every 30 s.
 *
 *   🔴 **The fifth tile „Integrations" is GONE** (G14a-3, plan §2.6/Q1,
 *   Orchestrator decision 2026-10-21 A). It showed `${green}/${total}` from a
 *   `v1-stub` — `{"green":0,"total":0,"phase":"v1-stub"}` (`dashboard.py:104`)
 *   — i.e. a confident `0/0` for an unknown quantity, which Regel 22 §3
 *   forbids. The real number lives on `/admin/integrations`. It also made the
 *   required mobile form „2×2" unreachable: five tiles are three rows.
 *
 *   🔴 **No horizontal scrolling strip any more** (plan §2.6). Until G14a-3 the
 *   tiles sat in a `Box` with `overflowX: 'auto'` under `sm` — a „carousel"
 *   that hid two of five tiles behind a swipe. `KpiRow` gives exactly two
 *   tracks under `sm` (`KpiRow.jsx`, Nachtrag 2026-10-08), so four tiles are
 *   2×2 and nothing is off-screen.
 *
 *   🔴 **The tiles carry NO interaction** (`onClick` nowhere; OE-1 A, Owner
 *   2026-10-11, plan §6.3). A KPI that sets its own filter is a second
 *   navigation next to the sidebar, and P29 measures exactly that.
 *
 *   🔴 **„Unknown is never 0"** (Regel 22 §3) is the main case of this surface
 *   (plan §5.3, P10): a failed fetch leaves `kpis` undefined and every tile
 *   prints „—", never a zero. The one legitimate zero — no runs in the 24 h
 *   window — is worded, not coloured: „no runs in 24h" (`failedJobsKpi`).
 *
 * @useWhen
 *   - Entry point of the admin area (`/admin`)
 *   - Answering „is anything broken right now?" without opening a list
 *
 * @related
 *   - AdminLayout — wraps this page
 *   - useDashboardSummary — data source (30 s poll)
 *   - KpiRow / StatTile — the house form for a KPI row
 *   - RecentFailuresCard — the list underneath
 */
// exportiert fuer AdminDashboard.test.jsx (R15 H-5)
export const FMT_PCT = (v) => v == null ? '—' : formatPercent(v, { digits: 1 })

export function classify(rate, failedCount) {
  if (failedCount > 0) return 'error'
  // Keine Laeufe im Fenster heisst UNBEKANNT: die Karte zeigt „—" und darf
  // nicht gruen leuchten (Regel 22 §3, „Unknown is never 0").
  if (rate == null) return 'neutral'
  if (rate < 0.95) return 'error'
  if (rate < 0.99) return 'warning'
  return 'success'
}

/**
 * `classify`s vier Zustände auf die TONWÖRTER von `StatTile scale="v2"`.
 *
 * 🔴 EINE Abbildung, nicht vier Ternäre an den Kacheln: `StatTile` kennt
 * `plain | ok | warn | err | info` (Pill-Vokabular), `classify` spricht
 * `neutral | success | warning | error`. Die beiden Wortlisten sind
 * absichtlich getrennt — `classify` ist die Aussage, das hier ist ihre
 * Darstellung.
 */
const TON = { neutral: 'plain', success: 'ok', warning: 'warn', error: 'err' }

/** Der `StatTile`-Ton zu einem `classify`-Zustand. Unbekannt → `plain`. */
export function tonVon(zustand) {
  return TON[zustand] || 'plain'
}

/**
 * Ton und Unterzeile der Kachel „Failed jobs · 24h".
 *
 * 🔴 R15 H-3 (2026-10-06, Runde 1): `failed_jobs_24h === 0` allein ist KEINE
 *    Aussage. Ohne Laeufe im 24-h-Fenster ist die Null trivial — die Kachel
 *    behauptete trotzdem „all green" in Erfolgsgruen. Die Laufzahl steht im
 *    selben Umschlag: `sync_success_rate.count` (Backend
 *    `admin_v2/dashboard.py`, `sync_total`). Dieselbe Klasse wie 6e („Unknown
 *    is never 0", Regel 22 §3), eine Kachel weiter.
 *
 * Fehlt die Zahl, bleibt das alte Verhalten — lieber die alte Aussage als eine
 * geratene.
 */
export function failedJobsKpi(kpis) {
  if (!kpis) return { tone: 'neutral', sub: null }
  const runs = kpis.sync_success_rate?.count
  if (runs === 0) return { tone: 'neutral', sub: 'no runs in 24h' }
  if (kpis.failed_jobs_24h > 0) return { tone: 'error', sub: 'see failures below' }
  return { tone: 'success', sub: 'all green' }
}

export default function AdminDashboard() {
  const { data, loading, error, errorTitle } = useDashboardSummary()

  const kpis = data?.kpis
  const failedJobs = failedJobsKpi(kpis)
  const laedt = loading && !data

  return (
    <Stack spacing={SP.section}>
      {error && (
        <Alert severity="error" variant="outlined" title={errorTitle || undefined}>
          {/* 🔴 R15 W5: `useDashboardSummary` behaelt `data` bei einem Fehler —
              die Kacheln zeigen also weiter den letzten guten Stand. Ohne diesen
              Zusatz stand die rohe Meldung daneben und nichts sagte, dass die
              Zahlen ALT sind. Genau die Form, die `AuditFeed.jsx` fuer dieselbe
              Lage schon traegt. Beim ERSTEN Abruf ohne Daten gibt es keinen
              Stand, dann bleibt es bei der Meldung allein. */}
          {data
            ? `The summary could not be refreshed: ${error}. The figures below are the last successful load.`
            : error}
        </Alert>
      )}

      {/* 🔴 Die Zonenmarke der KPI-Reihe (`liste-kpi`, Konzept §15). Sie ist
          die Messfläche von P2 auf dieser Seite — hier gibt es keine Zeile und
          keine Karte, also keinen der beiden Wege von `messeP2`. */}
      <KpiRow data-testid="liste-kpi">
        <KpiCell>
          <StatTile
            scale="v2"
            loading={laedt}
            reserveHint
            label="Sync success · 24h"
            value={FMT_PCT(kpis?.sync_success_rate?.value)}
            tone={tonVon(kpis ? classify(kpis.sync_success_rate.value, kpis.sync_success_rate.failed) : 'neutral')}
            hint={kpis
              ? `${kpis.sync_success_rate.count} runs · ${kpis.sync_success_rate.failed} failed`
              : '—'}
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            scale="v2"
            loading={laedt}
            reserveHint
            label="Failed jobs · 24h"
            value={kpis ? kpis.failed_jobs_24h : '—'}
            tone={tonVon(failedJobs.tone)}
            // 🔴 Die Unterzeile ist die WORTFORM desselben Zustands, den der Ton
            // färbt („no runs in 24h" / „all green" / „see failures below") —
            // P22: der Status steht nie über Farbe allein.
            hint={failedJobs.sub ?? '—'}
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            scale="v2"
            loading={laedt}
            reserveHint
            label="Webhooks · 24h"
            value={kpis ? kpis.webhooks_received_24h.count : '—'}
            tone={kpis && kpis.webhooks_received_24h.count === 0 ? 'err' : 'plain'}
            hint={kpis
              ? (kpis.webhooks_received_24h.last_received
                ? `last: ${formatBerlinTime(kpis.webhooks_received_24h.last_received)}`
                : 'none received — check Shopify')
              : '—'}
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            scale="v2"
            loading={laedt}
            reserveHint
            label="Email delivery · 24h"
            value={FMT_PCT(kpis?.email_delivery_rate?.value)}
            tone={tonVon(kpis ? classify(kpis.email_delivery_rate.value, kpis.email_delivery_rate.failed) : 'neutral')}
            hint={kpis
              ? `${kpis.email_delivery_rate.count} attempted · ${kpis.email_delivery_rate.failed} failed`
              : '—'}
          />
        </KpiCell>
      </KpiRow>

      {/* Recent failures.
          🔴 `?? null`, nicht `|| []`: Regel 22 §3 („Unknown is never `0`"). Ein
          gescheiterter Abruf liefert kein `data` — `|| []` machte daraus die
          leere Liste, und die Karte behauptete „No failures in the last 24
          hours." über einen Stand, den niemand kennt. `null` heisst UNBEKANNT
          und bekommt einen eigenen Satz. `data` mit `recent_failures: []`
          bleibt der grüne Fall. */}
      <RecentFailuresCard
        failures={data?.recent_failures ?? null}
        loading={laedt}
      />

      {/* 🔴 R16 W2: `text.disabled` liegt auf `background.paper` bei ≈2,7:1 und
          damit unter WCAG AA (4,5:1 fuer `FS.sm`). „Last checked" ist
          Information, nicht Dekoration. */}
      {data?.generated_at && (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>
          Last checked: {formatBerlinTime(data.generated_at)}
        </Typography>
      )}
    </Stack>
  )
}
